import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Part } from "../../models/Part";

export const MultipleDeleteModal = ({
	open,
	onClose,
	deleteFunction,
	selectedRows,
	entity,
}: {
	open: boolean;
	onClose: (deleted: boolean) => void;
	deleteFunction: (partId: string) => Promise<void>;
	selectedRows: Part[];
	entity?: { queryKey?: string; name?: string };
}) => {
	const [error, setError] = useState("");

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteFunction, {
		meta: {
			successMessage: `The ${entity?.name?.toLowerCase()}s were deleted successfully`,
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [`${entity?.queryKey ? entity?.queryKey : entity?.name}s`],
			});
			onClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	return (
		<>
			<MesfModal
				open={open}
				handleClose={() => onClose(false)}
				title={`Delete multiple ${entity?.name}s`}
			>
				<MesfModal.Content dividers sx={{ overflowY: "scroll" }}>
					<Typography sx={{ mb: 2 }}>
						Are you sure you want to delete {selectedRows.length}{" "}
						{entity?.name?.toLowerCase()}s?
					</Typography>
					{selectedRows.length > 0 && (
						<Typography variant="body2" sx={{ mt: 2 }} component="div">
							<strong>Selected {entity?.name?.toLowerCase()}s:</strong>
							<ul>
								{[...selectedRows]
									.sort((a, b) => a.PartName.localeCompare(b.PartName))
									.map((part) => (
										<li key={part.PartId}>
											{part.PartName} (ID: {part.PartId})
										</li>
									))}
							</ul>
						</Typography>
					)}
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={() => onClose(false)} color="inherit">
						Cancel
					</Button>
					<ButtonWithLoading
						isLoading={deleteMutation.isLoading}
						onClick={() => {
							const partIds = selectedRows.map((item) => item.PartId);
							const partIdsString = partIds.join(",");
							deleteMutation.mutate(partIdsString);
						}}
						color="error"
						variant="contained"
					>
						Delete
					</ButtonWithLoading>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title={`Error Deleting ${entity?.name}s`}
			/>
		</>
	);
};
